import { useState } from 'react';
import { toNY, toISODateString } from '../utils/weekDates';
import CollapsibleSection from './CollapsibleSection';

/**
 * My Home's "Today" / "This week" task feed — renders a user's own open
 * WeekBoardItems (system-generated + manually assigned on Week at a
 * Glance). Shared by MyHomePM.js and SupplierPortal.js's "Your Tasks"
 * section (formerly MyHomeSupplier.js, folded into Supplier Portal
 * 2026-08-31) so the two roles' task cards can't drift into two different
 * implementations, the way this codebase's dual DeliveryCard history
 * already has.
 *
 * Props:
 *   tasks: [{ id, program_id, program_name, client_name, activity_date,
 *     activity_type, note, source ('system'|'manual'|'reminder'),
 *     owner_user_name, owner_supplier_name }] — from GET /home/pm or
 *     GET /home/supplier. 'reminder' is a synthetic, read-time-only entry
 *     (e.g. the weekly "add expected deliveries" nag) with no real
 *     WeekBoardItem id behind it — no checkbox, no snooze/dismiss menu,
 *     just a dot marker and a CTA; it clears itself on its own next read.
 *   onToggleCompleted(taskId, completed) — manual items' checkbox, AND
 *     (2026-09-01) system items' ⋮ menu "Mark complete" — same handler,
 *     always called with completed=true for the latter since a system
 *     item has no checkbox to uncheck.
 *   onSnooze(taskId, untilIsoDate) — system items only
 *   onDismiss(taskId) — system items only
 *   onCta(task) — CTA button click; the page decides what "go do this"
 *     means for a given activity_type/source
 *   ctaLabel?(task) => string — override the default per-activity label
 *   defaultThisWeekView?: 'list' | 'calendar' (default 'calendar', 2026-09-03
 *     — was 'list' before an explicit ask to default every caller to
 *     Calendar) — initial state of the toggle below.
 *   addDeliveryPrograms?: [{value, label}] — when supplied (alongside
 *     onAddDelivery), the Calendar view gains a small "+ Add" control per
 *     day letting the caller log an expected delivery right there (Supplier
 *     Portal only — omit both for a read-only calendar, e.g. MyHomePM.js).
 *   onAddDelivery?(programId, activityDateIso, note, expectedCount) —
 *     required alongside addDeliveryPrograms; POSTs the new Supplier
 *     delivery WeekBoardItem, same shape as SupplierPortal.js's own
 *     handleAddExpectedDelivery.
 *
 * "This week" is collapsed by default (2026-09-03, explicit ask — was always
 * expanded before) via the shared CollapsibleSection — EXCEPT when it's the
 * only bucket with anything in it (Today is empty but This week isn't,
 * 2026-09-03 follow-up ask — "so user can see the tasks in future"), in
 * which case it opens by default instead, since collapsing it would leave
 * nothing else visible on the whole feed. Has its own List/Calendar toggle
 * inside the body (local `thisWeekView` state, not a prop — the choice isn't
 * meant to persist across visits, only its initial value is configurable via
 * defaultThisWeekView, which itself now defaults to 'calendar' — "on click
 * to open This week, default to calendar view").
 * Calendar mode renders `WeekCalendar`, a read-mostly Mon-Fri mini-grid — a
 * paired-down personal slice of Week at a Glance's own board, scoped to just
 * this feed's own tasks. No note text, no owner, no drag/edit/CTA/snooze/
 * dismiss beyond the optional add-delivery control above — a manual item's
 * checkbox is otherwise the only interactive control there. "Today" is
 * unaffected by the toggle; it's always the plain list. When
 * addDeliveryPrograms is supplied, "This week" renders even with zero tasks,
 * since the whole point is a place to add one.
 *
 * "Today" excludes manually-assigned Week at a Glance items entirely
 * (2026-09-03, real ask — "confusing users because there is no real action
 * yet until there is actual data") — a manual WAAG item is a PM/supplier's
 * own free-text plan, not a live condition backed by real data the way every
 * system item is, so it has no place demanding attention in a "here's what
 * needs doing today" list. It still shows in "This week" (still useful there
 * as a heads-up), just never in "Today".
 */
const ACTIVITY_CTA_DEFAULTS = {
  'Scrub': 'Start Lead QA',
  'Call reviews': 'Review Calls',
  'Client delivery': 'Download & Deliver',
  'Supplier delivery': 'View Delivery',
  'Rejected feedback': 'Download & Review',
  'Add expected deliveries': 'Add Deliveries',
};

function defaultCtaLabel(task) {
  if (task.source === 'manual') return 'Open Week at a Glance';
  return ACTIVITY_CTA_DEFAULTS[task.activity_type] || 'Open';
}

// NY-anchored, not browser-local (2026-09-01, real bug fix) — this used to
// build "today" from a plain `new Date()` in whatever timezone the viewer's
// browser happened to be in, while WeekAtAGlance.js's own week-boundary math
// (weekDates.js's getWeekStart/toNY) has always been explicitly anchored to
// America/New_York. A supplier outside that timezone could see a different
// "this week" here than Week at a Glance's default view does — reported as
// "I added deliveries, but they don't show up on Week at a Glance" — most
// visibly right at the week boundary, where the two clocks can disagree
// about which Monday it even is. Reusing toNY()/toISODateString() from
// weekDates.js instead of hand-rolling local-time formatting keeps this
// page and Week at a Glance agreeing on "today" for every viewer.
function isoToday() {
  return toISODateString(toNY(new Date()));
}

function addDays(isoDate, days) {
  const [y, m, d] = isoDate.split('-').map(Number);
  return toISODateString(new Date(y, m - 1, d + days));
}

function mondayOfWeek(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay(); // 0=Sun..6=Sat
  dt.setDate(dt.getDate() + (dow === 0 ? -6 : 1 - dow));
  return toISODateString(dt);
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

/**
 * A day cell's "+ Add" control — collapsed to a small text button until
 * clicked, then a compact inline form (program select + optional note +
 * optional expected count). Only ever rendered when the caller (Supplier
 * Portal) supplies addDeliveryPrograms/onAddDelivery — PM's usage of
 * WeekCalendar never passes them, so this never renders there.
 */
function AddDeliveryControl({ dateIso, programs, onAdd }) {
  const [adding, setAdding] = useState(false);
  const [programId, setProgramId] = useState(programs[0]?.value || '');
  const [note, setNote] = useState('');
  const [count, setCount] = useState('');
  const [saving, setSaving] = useState(false);

  if (!adding) {
    return (
      <button
        onClick={() => setAdding(true)}
        style={{ border: '1px dashed #cbd3d9', background: 'transparent', color: '#9aa5ab', borderRadius: '5px', padding: '3px 7px', fontSize: '10.5px', cursor: 'pointer', width: '100%', textAlign: 'left' }}
      >
        + Add deliveries
      </button>
    );
  }

  const submit = async () => {
    if (!programId) return;
    setSaving(true);
    try {
      const parsedCount = count.trim() === '' ? null : parseInt(count, 10);
      await onAdd(Number(programId), dateIso, note.trim() || null, Number.isNaN(parsedCount) ? null : parsedCount);
      setAdding(false);
      setNote('');
      setCount('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '6px', background: '#f8f9fa', border: '1px solid #e0e4e8', borderRadius: '6px' }}>
      <select value={programId} onChange={e => setProgramId(e.target.value)}
        style={{ fontSize: '10.5px', padding: '3px 4px', border: '1px solid #ddd', borderRadius: '4px' }}>
        {programs.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
      <input type="number" min="0" placeholder="Est. # leads" value={count} onChange={e => setCount(e.target.value)}
        style={{ fontSize: '10.5px', padding: '3px 4px', border: '1px solid #ddd', borderRadius: '4px' }} />
      <input type="text" placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)}
        style={{ fontSize: '10.5px', padding: '3px 4px', border: '1px solid #ddd', borderRadius: '4px' }} />
      <div style={{ display: 'flex', gap: '4px' }}>
        <button onClick={submit} disabled={saving || !programId}
          style={{ flex: 1, fontSize: '10.5px', padding: '3px 0', background: saving ? '#ccc' : '#27ae60', color: 'white', border: 0, borderRadius: '4px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '700' }}>
          {saving ? '…' : 'Add'}
        </button>
        <button onClick={() => setAdding(false)}
          style={{ flex: 1, fontSize: '10.5px', padding: '3px 0', background: 'white', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * A mini, read-mostly Mon-Fri grid — a paired-down personal slice of Week
 * at a Glance (that page's own full board), scoped to just the signed-in
 * user's/supplier's own tasks. Takes the FULL `tasks` array (not just the
 * future-only "This week" bucket) so today's own column isn't empty — a
 * task dated today needs to show in its Wednesday-when-today-is-Wednesday
 * cell here even though the flat "Today" list above already covers it too;
 * omitting today's items left a delivery added "for today" appearing to
 * vanish (it landed in the separate Today list instead, not the calendar
 * at all — a real reported gap, 2026-08-31). No note text, no owner, no
 * drag/edit/snooze/dismiss — a manual item's checkbox is the only
 * interactive control, same handler as the list view's. The one exception
 * is the optional per-day "+ Add" control (see AddDeliveryControl above) —
 * Supplier Portal's own way to log an expected delivery straight from the
 * calendar, gated entirely on addDeliveryPrograms/onAddDelivery being
 * supplied.
 */
function WeekCalendar({ tasks, onToggleCompleted, addDeliveryPrograms, onAddDelivery }) {
  const monday = mondayOfWeek(isoToday());
  const days = WEEKDAY_LABELS.map((label, i) => {
    const iso = addDays(monday, i);
    return { label, iso, dayTasks: tasks.filter(t => t.activity_date === iso) };
  });
  const canAddDelivery = !!(addDeliveryPrograms && addDeliveryPrograms.length > 0 && onAddDelivery);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
      {days.map(day => (
        <div key={day.iso} style={{ background: 'white', border: '1px solid #e0e4e8', borderRadius: '8px', padding: '8px', minHeight: '92px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ fontSize: '10.5px', fontWeight: '700', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            {day.label} <span style={{ fontWeight: '400', color: '#c7ccd1' }}>{day.iso.slice(5)}</span>
          </div>
          {day.dayTasks.length === 0 ? (
            <div style={{ fontSize: '11px', color: '#d5d9dc' }}>—</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {day.dayTasks.map(t => {
                const isManual = t.source === 'manual';
                return (
                  <label key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '5px', fontSize: '11px', lineHeight: '1.3', color: '#2c3e50', cursor: isManual ? 'pointer' : 'default' }}>
                    {isManual ? (
                      <input
                        type="checkbox"
                        checked={!!t.completed}
                        onChange={(e) => onToggleCompleted(t.id, e.target.checked)}
                        style={{ marginTop: '1px', width: '12px', height: '12px', cursor: 'pointer', flexShrink: 0 }}
                      />
                    ) : (
                      <span style={{ color: '#bbb', marginTop: '1px', flexShrink: 0 }}>●</span>
                    )}
                    <span style={{ textDecoration: isManual && t.completed ? 'line-through' : 'none', opacity: isManual && t.completed ? 0.5 : 1, wordBreak: 'break-word' }}>
                      {t.activity_type}{t.program_name ? ` · ${t.program_name}` : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {canAddDelivery && (
            <div style={{ marginTop: 'auto' }}>
              <AddDeliveryControl dateIso={day.iso} programs={addDeliveryPrograms} onAdd={onAddDelivery} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Snooze/Dismiss (both purely personal — hide from my own view, never touch
// the underlying WeekBoardItem) plus, 2026-09-01, a genuine "Mark complete"
// — a system item (Scrub, Call reviews, ...) has no checkbox of its own, so
// before this there was no way to say "I actually did this" other than
// waiting for its real trigger condition to resolve. Real bug report this
// closes: checking off an unrelated "Supplier delivery" note used to
// silently complete a derived Scrub placeholder as a side effect — those
// are two separate actions now (see home_triggers.py's
// _expected_delivery_items() docstring), so Scrub needs its OWN honest way
// to be marked done by hand.
function TaskMenu({ onSnooze, onDismiss, onComplete }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title="Snooze, dismiss, or mark complete"
        style={{ border: '1px solid transparent', background: 'transparent', color: '#95a5a6', cursor: 'pointer', fontSize: '16px', padding: '2px 6px', borderRadius: '4px' }}
      >
        ⋮
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'absolute', top: '26px', right: 0, background: 'white', border: '1px solid #e0e4e8', borderRadius: '8px', boxShadow: '0 4px 14px rgba(0,0,0,0.1)', padding: '4px', zIndex: 5, minWidth: '170px' }}
        >
          <button
            onClick={() => { onComplete(); setOpen(false); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '7px 9px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
          >
            ✓ Mark complete
          </button>
          <button
            onClick={() => { onSnooze(addDays(isoToday(), 1)); setOpen(false); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '7px 9px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
          >
            Snooze until tomorrow
          </button>
          <button
            onClick={() => { onDismiss(); setOpen(false); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '7px 9px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

function TaskCard({ task, onToggleCompleted, onSnooze, onDismiss, onCta, ctaLabel }) {
  const isManual = task.source === 'manual';
  const isReminder = task.source === 'reminder';
  const label = (ctaLabel ? ctaLabel(task) : null) || defaultCtaLabel(task);
  const overdue = task.activity_date < isoToday();

  return (
    <div style={{
      background: 'white', border: '1px solid #e0e4e8', borderRadius: '10px',
      padding: '9px 13px', display: 'flex', alignItems: 'center', gap: '12px',
      borderLeft: `4px solid ${isManual ? '#8452c9' : (isReminder ? '#9a6b12' : (overdue ? '#e67e22' : '#3498db'))}`,
    }}>
      {isManual ? (
        <input
          type="checkbox"
          checked={!!task.completed}
          onChange={(e) => onToggleCompleted(task.id, e.target.checked)}
          style={{ width: '17px', height: '17px', cursor: 'pointer', flexShrink: 0 }}
        />
      ) : (
        <span title={isReminder ? 'Recurring reminder — clears on its own once you add the item' : 'System-triggered — clears automatically once the underlying work is done'}
          style={{ color: '#bbb', fontSize: '13px', flexShrink: 0, width: '17px', textAlign: 'center' }}>●</span>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: '700', fontSize: '13px', color: '#2c3e50' }}>{task.program_name}</span>
          {task.client_name && <span style={{ color: '#aaa', fontSize: '11.5px' }}>{task.client_name}</span>}
        </div>
        <div style={{ fontSize: '12.5px', color: '#555', marginTop: '2px' }}>
          {task.activity_type}{task.note ? ` — ${task.note}` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '5px', flexWrap: 'wrap' }}>
          {/* The purple "Week at a Glance" pill on manual items was removed
              2026-08-31 (explicit ask — visually distracting, not helping).
              System/reminder items keep their Auto/Weekly badge. */}
          {!isManual && (
            <span style={{
              fontSize: '10px', fontWeight: '700', letterSpacing: '0.03em', textTransform: 'uppercase',
              padding: '2px 7px', borderRadius: '999px',
              background: isReminder ? '#fdf1de' : '#eaf4fb',
              color: isReminder ? '#9a6b12' : '#3498db',
            }}>
              {isReminder ? 'Weekly' : 'Auto'}
            </span>
          )}
          <span style={{ fontSize: '11.5px', color: '#aaa' }}>
            {task.activity_date}{overdue && !isManual && !isReminder ? ' · overdue' : ''}
          </span>
        </div>
      </div>

      <button
        onClick={() => onCta(task)}
        style={{ flexShrink: 0, border: 0, borderRadius: '6px', padding: '8px 13px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', background: '#2c3e50', color: 'white', whiteSpace: 'nowrap' }}
      >
        {label}
      </button>

      {!isManual && !isReminder && (
        <TaskMenu
          onComplete={() => onToggleCompleted(task.id, true)}
          onSnooze={(untilIso) => onSnooze(task.id, untilIso)}
          onDismiss={() => onDismiss(task.id)}
        />
      )}
    </div>
  );
}

export default function TaskList({
  tasks, onToggleCompleted, onSnooze, onDismiss, onCta, ctaLabel,
  defaultThisWeekView = 'calendar', addDeliveryPrograms, onAddDelivery,
}) {
  const [thisWeekView, setThisWeekView] = useState(defaultThisWeekView);
  const today = isoToday();
  // Manual items excluded from Today (real ask, 2026-09-03) — see the
  // module docstring above. A manual item dated today or earlier can't just
  // fall through both buckets, though (Today excludes it, and the plain
  // `activity_date > today` test that defines "this week" would too) — it
  // always lands in "This week" instead regardless of its own date, since a
  // free-text WAAG plan isn't time-critical the way a real, data-backed
  // system item is; the calendar (which reads the full `tasks` array, not
  // this bucket) still places it on its own actual day.
  const todayTasks = tasks.filter(t => t.activity_date <= today && t.source !== 'manual');
  const weekTasks = tasks.filter(t => t.source === 'manual' || t.activity_date > today);
  const canAddDelivery = !!(addDeliveryPrograms && addDeliveryPrograms.length > 0 && onAddDelivery);
  // "This week" opens by default when it's the ONLY bucket with anything in
  // it (2026-09-03, explicit ask — "so user can see the tasks in future")
  // — Today is empty but This week isn't, so there's nothing else on this
  // feed to look at and collapsing it would hide the one thing there is to
  // see. Stays collapsed in every other case (both empty, both have
  // something, or only Today does) — unchanged from the just-shipped
  // collapse-by-default behavior.
  const thisWeekIsOnlySectionWithTasks = todayTasks.length === 0 && weekTasks.length > 0;

  if (tasks.length === 0 && !canAddDelivery) {
    return (
      <div style={{ padding: '24px', background: 'white', border: '1px dashed #e0e4e8', borderRadius: '10px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>
        ✅ Nothing outstanding right now.
      </div>
    );
  }

  const section = (label, list) => list.length > 0 && (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: '#95a5a6', marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {list.map(t => (
          <TaskCard key={t.id} task={t} onToggleCompleted={onToggleCompleted} onSnooze={onSnooze} onDismiss={onDismiss} onCta={onCta} ctaLabel={ctaLabel} />
        ))}
      </div>
    </div>
  );

  // Deliberately low visual weight — a display-mode switch, not a primary
  // action. Previously a bordered pill matching the CTA buttons' own dark
  // navy fill, which read as another button competing for attention rather
  // than a quiet toggle (explicit ask, 2026-09-01). Text-only, small, with
  // just an underline marking the active mode.
  const viewToggleButton = (mode, label) => (
    <button
      onClick={() => setThisWeekView(mode)}
      style={{
        border: 'none', borderBottom: thisWeekView === mode ? '2px solid #9aa5ab' : '2px solid transparent',
        background: 'transparent', borderRadius: 0, padding: '1px 1px 2px', fontSize: '10.5px',
        fontWeight: thisWeekView === mode ? '700' : '500', letterSpacing: '0.02em',
        cursor: 'pointer', color: thisWeekView === mode ? '#5c6a75' : '#c2c9ce',
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      {section('Today', todayTasks)}
      {(weekTasks.length > 0 || canAddDelivery) && (
        <div id="this-week-tasks">
          {/* Collapsed by default (2026-09-03, explicit ask — was always
              expanded) via the shared CollapsibleSection; the List/Calendar
              toggle moved inside the body since it's meaningless before the
              section is even open. */}
          <CollapsibleSection label="This week" count={weekTasks.length || undefined} defaultOpen={thisWeekIsOnlySectionWithTasks}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              {viewToggleButton('list', 'List')}
              {viewToggleButton('calendar', 'Calendar')}
            </div>
            {thisWeekView === 'list' ? (
              weekTasks.length === 0 ? (
                <div style={{ padding: '16px', background: 'white', border: '1px dashed #e0e4e8', borderRadius: '10px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>
                  Nothing scheduled — switch to Calendar to add an expected delivery.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  {weekTasks.map(t => (
                    <TaskCard key={t.id} task={t} onToggleCompleted={onToggleCompleted} onSnooze={onSnooze} onDismiss={onDismiss} onCta={onCta} ctaLabel={ctaLabel} />
                  ))}
                </div>
              )
            ) : (
              <WeekCalendar tasks={tasks} onToggleCompleted={onToggleCompleted} addDeliveryPrograms={addDeliveryPrograms} onAddDelivery={onAddDelivery} />
            )}
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
}
